// src/pages/JugaadHome.jsx
import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTractor,
  faCogs,
  faHardHat,
  faIndustry,
  faGraduationCap,
  faPlane,
  faHeartbeat,
  faEllipsisH,
  faPlayCircle,
  faPhone,
  faCommentDots,
  faShoppingCart,
  faArrowLeft,
  faHome,
} from "@fortawesome/free-solid-svg-icons";

const CATEGORY_TABS = [
  { id: "farm", label: "Farm", icon: faTractor },
  { id: "technical", label: "Technical", icon: faCogs },
  { id: "labour", label: "Labour", icon: faHardHat },
  { id: "factory", label: "Factory", icon: faIndustry },
  { id: "education", label: "Education", icon: faGraduationCap },
  { id: "household", label: "Home", icon: faHome },
  { id: "travel", label: "Travel", icon: faPlane },
  { id: "health", label: "Health", icon: faHeartbeat },
  { id: "misc", label: "Misc", icon: faEllipsisH },
];

// Dummy Data
const DUMMY_JUGAAD = {
  farm: [
    { id: 1, title: "Solar Irrigation Pump", desc: "Low-cost pump made from scrap solar cells." },
    { id: 2, title: "Seed Dispenser", desc: "Manual dispenser using old PVC pipes." },
  ],
  technical: [
    { id: 3, title: "Phone Charger Pedal", desc: "Charges devices while cycling." },
    { id: 4, title: "DIY Drone", desc: "Mini drone from old toy motors." },
  ],
  labour: [
    { id: 5, title: "Brick Carrier Tool", desc: "Makes brick lifting easy and safe." },
  ],
  factory: [
    { id: 6, title: "Waste Heat Reuse", desc: "Recycle heat from exhaust to preheat water." },
  ],
  education: [
    { id: 7, title: "Smart Blackboard", desc: "DIY projector setup for rural classrooms." },
  ],
  household: [
    { id: 8, title: "Plastic Bottle Broom", desc: "Eco-friendly broom made from reused plastic bottles." },
    { id: 9, title: "Cooling Fan from Old CPU", desc: "Converted computer fan for portable cooling." },
    { id: 10, title: "Soap Holder Drain Fix", desc: "Uses old toothbrush handle to make self-draining soap tray." },
    { id: 11, title: "Water Filter from Charcoal", desc: "Homemade water filter using sand, gravel, and charcoal." },
    { id: 12, title: "Portable Clothes Dryer", desc: "DIY drying rack made with bicycle wheel spokes." },
  ],
  travel: [
    { id: 13, title: "Portable Bed", desc: "Foldable bed that fits inside a bike carrier." },
  ],
  health: [
    { id: 14, title: "Low-cost Sanitizer Dispenser", desc: "Made using old foot pedal mechanism." },
  ],
  misc: [
    { id: 15, title: "Bottle Lamp", desc: "LED lamp made from plastic bottles." },
  ],
};

export default function JugaadHome() {
  const [activeTab, setActiveTab] = useState("farm");
  const [selectedItem, setSelectedItem] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");

  const handleAddComment = () => {
    if (newComment.trim()) {
      setComments([...comments, newComment]);
      setNewComment("");
    }
  };

  const currentList = DUMMY_JUGAAD[activeTab] || [];

  return (
    <div className="min-h-screen bg-black text-white p-3">
      <h1 className="text-2xl font-bold text-red-500 mb-3 text-center">
        💡 Jugaad Hub
      </h1>

      {/* Tabs */}
      {!selectedItem && (
        <div className="grid grid-cols-4 sm:grid-cols-4 md:grid-cols-8 gap-2 mb-4">
          {CATEGORY_TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex flex-col items-center justify-center p-2 rounded-xl text-xs sm:text-sm transition-all ${
                activeTab === tab.id
                  ? "bg-red-700 text-white"
                  : "bg-gray-800 hover:bg-red-800"
              }`}
            >
              <FontAwesomeIcon icon={tab.icon} className="text-lg mb-1" />
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* List View */}
      {!selectedItem && (
        <div className="space-y-2">
          {currentList.map((item) => (
            <div
              key={item.id}
              onClick={() => setSelectedItem(item)}
              className="bg-gray-900 p-3 rounded-xl hover:bg-gray-800 cursor-pointer transition-all"
            >
              <h2 className="text-lg font-semibold text-red-400">{item.title}</h2>
              <p className="text-sm text-gray-400">{item.desc}</p>
            </div>
          ))}
        </div>
      )}

      {/* Detail View */}
      {selectedItem && (
        <div className="bg-gray-900 p-4 rounded-xl mt-3">
          <button
            onClick={() => {
              setSelectedItem(null);
              setComments([]);
            }}
            className="flex items-center mb-3 text-gray-400 hover:text-white"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
            Back
          </button>

          <h2 className="text-xl font-bold text-red-400 mb-2">{selectedItem.title}</h2>
          <p className="text-gray-300 mb-4">{selectedItem.desc}</p>

          <div className="flex flex-wrap gap-3 mb-4">
            <button className="bg-red-700 hover:bg-red-600 px-3 py-2 rounded-lg flex items-center gap-2">
              <FontAwesomeIcon icon={faPlayCircle} /> Watch
            </button>
            <button className="bg-gray-800 hover:bg-gray-700 px-3 py-2 rounded-lg flex items-center gap-2">
              <FontAwesomeIcon icon={faPhone} /> Contact
            </button>
            <button className="bg-gray-800 hover:bg-gray-700 px-3 py-2 rounded-lg flex items-center gap-2">
              <FontAwesomeIcon icon={faShoppingCart} /> Buy Now
            </button>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-2">💬 Comments</h3>
            <div className="space-y-1 mb-2">
              {comments.length > 0 ? (
                comments.map((c, i) => (
                  <p key={i} className="bg-gray-800 p-2 rounded-lg text-sm">
                    <FontAwesomeIcon icon={faCommentDots} className="mr-2 text-red-500" />
                    {c}
                  </p>
                ))
              ) : (
                <p className="text-gray-500 text-sm">No comments yet.</p>
              )}
            </div>

            <div className="flex gap-2">
              <input
                type="text"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment..."
                className="flex-grow bg-gray-800 text-white px-3 py-2 rounded-lg outline-none"
              />
              <button
                onClick={handleAddComment}
                className="bg-red-700 hover:bg-red-600 px-4 py-2 rounded-lg text-white font-semibold"
              >
                Post
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
